/**
 * Writes the four client-detail fields (clientName, clientDob,
 * pastMedicalHistory, surgicalProcedures) for the chronology Details modal.
 *
 * Important: this endpoint accepts those four fields *flat* in the body
 * (no `additionalInfo` wrapper, despite the GET nesting them there) and
 * *rejects* unrelated fields silently — in particular `gRef` must NOT be
 * sent here, even though it appears in the same modal. Send `gRef` via
 * `case/edit` instead. See ActionsPanel.handleSaveDetails for the split.
 */
import { CASE_METADATA_TAG, CASES_TAG } from '../constants';
import { api } from '../core';
import type { AdditionalInfo } from './getCase';

export type CaseInfoEditResponse = object;

export type CaseInfoEditProps = { caseId: string } & Partial<AdditionalInfo>;

const caseInfoEditApi = api.injectEndpoints({
  endpoints: (builder) => ({
    postCaseInfoEdit: builder.mutation<CaseInfoEditResponse, CaseInfoEditProps>({
      query: (props: CaseInfoEditProps) => {
        const body: Record<string, unknown> = {};
        Object.entries(props).forEach(([key, value]) => {
          if (value !== undefined) {
            body[key] = value;
          }
        });

        return {
          url: 'case/case-info/edit',
          method: 'POST',
          body: body,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags([CASES_TAG, CASE_METADATA_TAG]));
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in postCaseInfoEdit in onQueryStarted:', error);
          }
        }
      },
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

export const { usePostCaseInfoEditMutation } = caseInfoEditApi;
